/**
 * Intro tour (Sept 2026): what everyone sees once, on first open after the
 * update. A splash, five slides that build up in the middle of the screen (the
 * way the What's New demos do), then either sign-up (logged out) or
 * "Start splitting" (already a member).
 *
 * Slides and their animation scripts live in ./scenes. This file runs them:
 * one slide at a time, Continue / swipe, and each slide's stage scaled to fit
 * shorter phones. Slides hold on their last frame; only the splash moves on by
 * itself. No Skip: everyone sees every slide, then answers where they heard
 * about us before the sign-in buttons unlock.
 */
import { useEffect, useRef } from "react";
import { track } from "@/lib/analytics";
import { isIosNative } from "@/lib/iap";
import { buildSlides, type FinishAction, type HeardFrom, type SceneCtx } from "./scenes";
import meadow from "./meadow.webp";
import "./intro.css";

// The tallest stage content, at design width. Stages scale down to fit it.
const STAGE_W = 339;
const STAGE_H = 410;

const reducedMotion = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function IntroTour({ member, onFinish }: {
  /** Already signed in: ends on "Start splitting" instead of sign-up. */
  member: boolean;
  onFinish: (action: FinishAction, heard: { source: HeardFrom; detail?: string }) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;

  useEffect(() => {
    const root = rootRef.current!;
    const slides = buildSlides({ member, ios: isIosNative, meadow });
    const last = slides.length - 1;
    const dotted = slides.map((s, i) => (s.dots ? i : -1)).filter((i) => i >= 0);
    const slidesEl = root.querySelector(".slides") as HTMLElement;
    const foot = root.querySelector(".foot") as HTMLElement;
    const dots = root.querySelector(".dots") as HTMLElement;
    dots.innerHTML = dotted.map(() => "<b></b>").join("");
    slidesEl.innerHTML = slides.map((_, k) => `<section class="slide" data-k="${k}" aria-hidden="true"></section>`).join("");

    let current = -1;
    let run = { dead: false, timers: [] as number[], leave: [] as (() => void)[] };
    const stopRun = () => { run.dead = true; run.timers.forEach(clearTimeout); run.leave.forEach((fn) => fn()); run.leave = []; };
    let observer: ResizeObserver | null = null;
    const rm = reducedMotion();
    track("intro_started", { member });

    const fitStage = (el: HTMLElement) => {
      const stage = el.querySelector(".stage") as HTMLElement | null;
      const box = el.querySelector(".fitbox") as HTMLElement | null;
      if (!stage || !box) return;
      const fit = () => {
        const s = Math.min(1, (stage.clientWidth - 36) / STAGE_W, stage.clientHeight / STAGE_H);
        box.style.transform = `scale(${Math.max(0.5, s)})`;
      };
      fit();
      observer?.disconnect();
      observer = new ResizeObserver(fit);
      observer.observe(stage);
    };

    const build = (k: number) => {
      const s = slides[k];
      const el = slidesEl.querySelector(`[data-k="${k}"]`) as HTMLElement;
      if (s.h) {
        const words = s.h.split(" ").map((w, j) => `<span class="w" style="transition-delay:${j * 55}ms">${w}</span>`).join(" ");
        const ticks = s.ticks?.length ? `<div class="ticks">${s.ticks.map((t) => `<span class="tk"><i>${"<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><path d='M20 6 9 17l-5-5'/></svg>"}</i>${t}</span>`).join("")}</div>` : "";
        el.innerHTML = `<div class="stage" aria-hidden="true"><div class="fitbox">${s.html()}</div></div>
          <div class="copy"><h3>${words}</h3>${s.sub ? `<p class="sub">${s.sub}</p>` : ""}${ticks}</div>`;
      } else {
        el.innerHTML = s.html();
      }
      return el;
    };

    const go = (k: number, back = false) => {
      k = Math.max(0, Math.min(last, k));
      if (k === current) return;
      stopRun();
      const prev = current >= 0 ? (slidesEl.querySelector(`[data-k="${current}"]`) as HTMLElement) : null;
      if (prev) {
        prev.classList.remove("on");
        prev.classList.toggle("back", back);
        prev.classList.add("gone");
        prev.setAttribute("aria-hidden", "true");
        window.setTimeout(() => { if (!prev.classList.contains("on")) { prev.classList.remove("gone", "back"); prev.innerHTML = ""; } }, 650);
      }
      current = k;
      const s = slides[k];
      const el = build(k);
      el.classList.remove("gone");
      el.classList.toggle("back", back);
      void el.offsetWidth;
      el.classList.add("on");
      el.classList.remove("back");
      el.setAttribute("aria-hidden", "false");
      fitStage(el);
      foot.classList.toggle("hide", k === 0 || k === last);
      dots.querySelectorAll("b").forEach((b, j) => b.classList.toggle("on", dotted[j] === k));
      window.setTimeout(() => {
        el.querySelector("h3")?.classList.add("in");
        el.querySelector(".copy")?.classList.add("in");
      }, 220);
      track("intro_slide_viewed", { slide: s.key, member });

      const thisRun = (run = { dead: false, timers: [] as number[], leave: [] as (() => void)[] });
      const sleep = (ms: number) => new Promise<void>((r) => { thisRun.timers.push(window.setTimeout(r, ms)); });
      const c: SceneCtx = {
        q: ((sel: string) => el.querySelector(sel)) as SceneCtx["q"],
        qa: ((sel: string) => el.querySelectorAll(sel)) as SceneCtx["qa"],
        async later(ms) { await sleep(rm ? 0 : ms); if (thisRun.dead) throw "cancelled"; },
        tick: (n) => { el.querySelectorAll(".tk")[n]?.classList.add("on"); },
        onLeave: (fn) => { if (thisRun.dead) fn(); else thisRun.leave.push(fn); },
      };
      (async () => {
        try {
          await s.run(c);
          if (k === 0 && !thisRun.dead) go(1);
        } catch (e) {
          if (e !== "cancelled") console.error(e);
        }
      })();
    };

    // Picking where they heard about us. Other focuses its field right away
    // (inside the tap, so iOS opens the keyboard), which opens the typing view.
    const pick = (opt: HTMLElement) => {
      const cloud = opt.parentElement!;
      if (cloud.dataset.pick !== opt.dataset.src) {
        cloud.dataset.pick = opt.dataset.src;
        cloud.querySelectorAll("[data-src]").forEach((o) => { o.classList.toggle("on", o === opt); o.setAttribute("aria-checked", String(o === opt)); });
        if (!rm) opt.animate([{ transform: "scale(1)" }, { transform: "scale(.95)" }, { transform: "none" }], { duration: 320, easing: "cubic-bezier(.2,.75,.2,1)" });
        root.querySelector(".su")?.classList.remove("locked");
      }
      if (opt.dataset.src === "other") { root.classList.add("typing"); (root.querySelector(".other") as HTMLInputElement).focus(); }
    };

    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (root.classList.contains("typing")) {
        // A tap outside the field closes the keyboard (iOS won't on its own).
        if (!t.closest(".hp.oth")) { root.classList.remove("typing"); (root.querySelector(".other") as HTMLInputElement).blur(); }
        return;
      }
      const finish = t.closest("[data-finish]") as HTMLElement | null;
      if (finish) {
        const cloud = root.querySelector(".hcloud") as HTMLElement | null;
        const source = cloud?.dataset.pick as HeardFrom | undefined;
        if (!source) return; // locked until they answer
        const detail = source === "other" ? (root.querySelector(".other") as HTMLInputElement).value.trim() || undefined : undefined;
        const action = finish.dataset.finish as FinishAction;
        track("intro_heard_from", { source, detail, member });
        track("intro_finished", { action, member, heard_from: source });
        finishRef.current(action, { source, detail });
        return;
      }
      const opt = t.closest("[data-src]") as HTMLElement | null;
      if (opt) { pick(opt); return; }
      if (t.closest(".cont")) go(current + 1);
      else if (current === 0) go(1); // tap anywhere on the splash to move on
    };
    let startX = 0, startY = 0;
    const onTouchStart = (e: TouchEvent) => { startX = e.touches[0].clientX; startY = e.touches[0].clientY; };
    const onTouchEnd = (e: TouchEvent) => {
      const dx = e.changedTouches[0].clientX - startX, dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) || root.classList.contains("typing")) return;
      if (dx < 0 && current < last) go(current + 1);
      if (dx > 0 && current > 1) go(current - 1, true);
    };
    // Typing in Other: the keyboard shrinks the screen, so the question and the
    // field move up together (intro.css .typing) until it closes.
    const onFocusIn = (e: FocusEvent) => { if ((e.target as HTMLElement).matches?.(".other")) root.classList.add("typing"); };
    const onFocusOut = (e: FocusEvent) => { if ((e.target as HTMLElement).matches?.(".other")) root.classList.remove("typing"); };
    const onKeyDown = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === "Enter" && t.matches?.(".other")) (t as HTMLInputElement).blur();
      else if ((e.key === "Enter" || e.key === " ") && t.matches?.(".hp.oth")) { e.preventDefault(); pick(t); }
    };
    root.addEventListener("focusin", onFocusIn);
    root.addEventListener("focusout", onFocusOut);
    root.addEventListener("keydown", onKeyDown);
    root.addEventListener("click", onClick);
    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchend", onTouchEnd);
    go(0);

    return () => {
      stopRun();
      observer?.disconnect();
      root.removeEventListener("focusin", onFocusIn);
      root.removeEventListener("focusout", onFocusOut);
      root.removeEventListener("keydown", onKeyDown);
      root.removeEventListener("click", onClick);
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchend", onTouchEnd);
    };
  }, [member]);

  return (
    <div ref={rootRef} className="sp-intro" data-testid="intro-tour">
      <div className="frame">
        <div className="slides" />
        <div className="foot hide">
          <div className="dots" aria-hidden="true" />
          <button className="cta cont" type="button" data-testid="intro-continue">Continue</button>
        </div>
      </div>
    </div>
  );
}
